import * as React from 'react';
import { PrimitiveType, useIntl } from 'react-intl';
import HistorySection from 'Components/HistorySection';
import HistoryRowListing from 'Components/HistoryRowListing';
import DocumentCreationButton from 'Components/DocumentCreationButton';
import HistoryRowControls from 'Components/HistoryRowControls';
import { Container, Item } from 'Components/Grid';
import { calculateFBIMODScore } from 'Routes/DiagnosticSurvey/utils';
import InfoPopover from 'Components/InfoPopover';
import { useAppSelector } from 'Store/index';
import { isByInformant } from 'Utility/documentHandling';
import { LocalizerCtx } from 'Containers/Localizer';
import { fbimod } from 'neuro-schemas';

const FBIMODHistory = ({ documents, startEdit, handleMyDelete, view }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string, values?: Record<string, PrimitiveType>) => formatMessage({ id }, values);

  const { locale } = React.useContext(LocalizerCtx);

  const localizations = fbimod.getLocalizations(locale ?? 'fi');
  const localize = (id: keyof typeof localizations): string => {
    const localized = localizations[id];
    // eslint-disable-next-line no-console
    if (!localized) console.error(`fbimod: couldn't localize id ${id}`);
    return localized ?? '';
  };

  const informants = useAppSelector((state: IState) => state.patient.informants) ?? [];

  return (
    <HistorySection
      headerText={localize('title')}
      newButton={
        <DocumentCreationButton
          name="fbimod"
          text={'diagnosticSurvey.fbimod.newFBIMOD'}
          onClick={startEdit({}, 'fbimod')}
          width={15}
          height={3}
          fontSize={14}
          alternate={true}
        />
      }
    >
      <HistoryRowListing
        documents={documents}
        headers={<Item xs={true}>{fm('diagnosticSurvey.fbimod.score')}</Item>}
        contentFormat={(d): React.JSX.Element => {
          const isByInf = isByInformant(d, informants);
          return (
            <Container>
              <Item xs={isByInf ? 2 : true}>
                {calculateFBIMODScore(d) ? (
                  fm('diagnosticSurvey.fbimod.scoreTotal', { value: calculateFBIMODScore(d) })
                ) : (
                  <InfoPopover text="general.notFilled" color="primary" />
                )}
              </Item>
              {isByInf ? <Item xs={true}>{fm('diagnosticSurvey.fbimod.byInformant')}</Item> : undefined}
            </Container>
          );
        }}
        makeDate
        makeControls
        historyRowControls={(d: IControlProps) => {
          const isByInf = isByInformant(d, informants);
          return (
            <HistoryRowControls
              document={d}
              startEdit={startEdit}
              view={view}
              enableButtons={isByInf ?? undefined}
              deleteHandler={isByInf ? () => handleMyDelete(d._id, d._type) : undefined}
              hideEditHistory={isByInf}
            />
          );
        }}
      />
    </HistorySection>
  );
};

interface IOwnProps {
  documents: IFBIMOD[];
  startEdit: (document: TAnyObject, name?: string) => (e: React.MouseEvent<Element, MouseEvent>) => void;
  handleMyDelete: (myDocId: string, docType: string) => Promise<void>;
  view?: IView;
}

export default FBIMODHistory;
